import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import ConfirmDialog from '../components/ConfirmDialog';
import EAuctionProcessModal from '../components/EAuctionProcessModal';
import { Eye, Shield, FileText } from 'lucide-react';

const EAuctionProcessPage = () => {
    const [processData, setProcessData] = useState({
        title: '',
        description: '',
        features: '[]',
        is_active: true
    });
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPrinciple, setEditingPrinciple] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null); // To track which item we are editing

    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, index: null });

    const LOAN_TYPE = 'e_auction_process';

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/admin/loan-overview/${LOAN_TYPE}`);
            if (response.data.data) {
                setProcessData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching E-Auction process:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // --- Header Settings Handlers ---

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setProcessData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const saveSettings = async () => {
        try {
            setSavingSettings(true);
            // Prepare data: only title/description change, features rely on current state
            const submitData = new FormData();
            submitData.append('loan_type', LOAN_TYPE);
            submitData.append('title', processData.title);
            submitData.append('description', processData.description);
            submitData.append('features', processData.features);
            submitData.append('is_active', processData.is_active ? '1' : '0');

            // Using existing ID if present
            const url = processData.id
                ? `/api/admin/loan-overview/${processData.id}`
                : '/api/admin/loan-overview';
            const method = processData.id ? axios.put : axios.post;

            await method(url, submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Section settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    // --- Principle Cards Handlers ---

    const getFeaturesArray = () => {
        try {
            return typeof processData.features === 'string'
                ? JSON.parse(processData.features)
                : processData.features || [];
        } catch (e) {
            return [];
        }
    };

    const updateFeatures = async (newFeatures) => {
        // Optimistic UI update
        const updatedData = { ...processData, features: JSON.stringify(newFeatures) };
        setProcessData(updatedData);

        // API Call
        try {
            const submitData = new FormData();
            submitData.append('loan_type', LOAN_TYPE);
            submitData.append('title', updatedData.title);
            submitData.append('description', updatedData.description);
            submitData.append('features', updatedData.features);
            submitData.append('is_active', updatedData.is_active ? '1' : '0');

            const url = processData.id
                ? `/api/admin/loan-overview/${processData.id}`
                : '/api/admin/loan-overview';
            const method = processData.id ? axios.put : axios.post;

            await method(url, submitData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            // If we are deleting, show toast
            // If saving from modal, modal handles closing, we show toast here if we want or let savePrinciple handle it
        } catch (error) {
            console.error('Error updating principles:', error);
            toast.error('Failed to sync changes');
            // Revert changes could go here if needed
            fetchData();
        }
    };

    const handleAddPrinciple = () => {
        setEditingPrinciple(null);
        setEditingIndex(null);
        setIsModalOpen(true);
    };

    const handleEditPrinciple = (principle, index) => {
        setEditingPrinciple(principle);
        setEditingIndex(index);
        setIsModalOpen(true);
    };

    const handleSavePrinciple = (principleData) => {
        const currentFeatures = getFeaturesArray();
        let newFeatures = [...currentFeatures];

        if (editingIndex !== null) {
            // Edit existing
            newFeatures[editingIndex] = principleData;
            toast.success('Principle updated');
        } else {
            // Add new
            newFeatures.push(principleData);
            toast.success('Principle added');
        }

        updateFeatures(newFeatures);
    };

    const handleDeletePrinciple = (index) => {
        setDeleteConfirm({ isOpen: true, index });
    };

    const confirmDelete = () => {
        const currentFeatures = getFeaturesArray();
        const newFeatures = currentFeatures.filter((_, i) => i !== deleteConfirm.index);

        updateFeatures(newFeatures);
        toast.success('Principle removed');
        setDeleteConfirm({ isOpen: false, index: null });
    };

    const renderIconPreview = (iconName) => {
        // Just text for now or simple preview logic if icons available globally
        return <span className="font-bold text-xs">{iconName}</span>;
    };


    return (
        <div className="space-y-8 pb-10">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-800">E-Auction Process Setup</h1>
                <p className="text-slate-500 mt-1">Manage content for "What is an E-Auction?" section</p>
            </div>

            {loading ? (
                <div className="animate-pulse space-y-6">
                    <div className="bg-white h-64 rounded-2xl"></div>
                    <div className="bg-white h-48 rounded-2xl"></div>
                </div>
            ) : (
                <>
                    {/* 1. Section Header Settings */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold text-slate-800">Section Header Settings</h2>
                            <button
                                onClick={saveSettings}
                                disabled={savingSettings}
                                className="bg-slate-800 hover:bg-slate-900 text-white px-6 py-2.5 rounded-xl font-semibold transition-all disabled:opacity-50 text-sm shadow-md"
                            >
                                {savingSettings ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={processData.title}
                                    onChange={handleSettingsChange}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 transition-all font-medium text-slate-700"
                                    placeholder="e.g. What is an E-Auction?"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Description</label>
                                <textarea
                                    name="description"
                                    rows={4}
                                    value={processData.description}
                                    onChange={handleSettingsChange}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 transition-all text-slate-600 leading-relaxed resize-none"
                                    placeholder="Enter the main description text..."
                                />
                            </div>
                        </div>
                    </div>

                    {/* 2. Principles Cards */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-slate-800">Key Principles</h2>
                            <button
                                onClick={handleAddPrinciple}
                                className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg text-sm"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Add Principle
                            </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {getFeaturesArray().map((principle, index) => (
                                <div
                                    key={index}
                                    className={`group relative bg-white border-2 border-${principle.color}-100 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
                                >
                                    {/* Card Content */}
                                    <div className="flex flex-col items-center text-center">
                                        <div className={`w-14 h-14 rounded-2xl bg-${principle.color}-50 flex items-center justify-center text-${principle.color}-600 mb-3 shadow-sm group-hover:scale-110 transition-transform`}>
                                            {renderIconPreview(principle.icon)}
                                        </div>

                                        {/* Status Badge */}
                                        <div className="mb-2">
                                            {principle.is_active !== false ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-700 border border-green-100 uppercase tracking-wide">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200 uppercase tracking-wide">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span> Inactive
                                                </span>
                                            )}
                                        </div>

                                        <h3 className="font-bold text-slate-800 text-lg mb-2 opacity-90">{principle.title}</h3>
                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-${principle.color}-50 text-${principle.color}-600 border border-${principle.color}-100`}>
                                            {principle.color}
                                        </span>
                                    </div>

                                    {/* Action Overlay (Visible on Hover for desktop, or always separate buttons) */}
                                    <div className="pt-6 mt-6 border-t border-slate-50 flex gap-2">
                                        <button
                                            onClick={() => handleEditPrinciple(principle, index)}
                                            className="flex-1 flex items-center justify-center gap-2 py-2 text-slate-600 hover:bg-slate-50 hover:text-primary-600 rounded-lg text-sm font-semibold transition-colors"
                                        >
                                            <PencilIcon className="w-4 h-4" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeletePrinciple(index)}
                                            className="flex-1 flex items-center justify-center gap-2 py-2 text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-lg text-sm font-semibold transition-colors"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {getFeaturesArray().length === 0 && (
                                <div className="col-span-full bg-white rounded-2xl p-12 text-center border-2 border-dashed border-slate-200">
                                    <p className="text-slate-400 font-medium">No principles added yet. Click "Add Principle" to get started.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Edit/Add Modal */}
            <EAuctionProcessModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSavePrinciple}
                principle={editingPrinciple}
            />

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, index: null })}
                onConfirm={confirmDelete}
                title="Delete Principle"
                message="Are you sure you want to delete this principle? This action cannot be undone."
            />
        </div>
    );
};

export default EAuctionProcessPage;
